import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import EmailsDisplay from '~/components/core/EmailsDisplay/EmailsDisplay';
import { FeatureEnabled } from '~/components/core/FeatureFlagLayoutSwitch/FeatureFlagSwitch';
import { PropertyDetailsRow } from '~/components/core/NewDesignSystem/PropertyDetails/PropertyDetailsRow';
import InlineIconButton from '~/components/InlineIconButton';
import Avatar from '~/components/ui/atoms/Avatar';
import Divider from '~/components/ui/atoms/Divider';
import Popover from '~/components/ui/atoms/Popover';
import Switch from '~/components/ui/atoms/Switch';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { reportAxiosError } from '~/Utils';

import CardDialog from '../CardDialog';
import { Label, PropertyDetails, Text } from '../core';
import { useCms } from '../hooks/useCms';
import { LockedIcon, MinusCircleIcon, SignOutIcon } from '../icons';
import { ReminderIcon } from '../icons/notifications';
import PencilIcon from '../icons/PencilIcon';
import SignatureIcon from '../icons/SignatureIcon';
import OutOfOfficeDialog from '../OutOfOfficeDialog';
import PersonalEmailSignatureDialog from '../PersonalEmailSignatureDialog';
import { ShareNotificationsContainer } from '../ShareNotifications';
import useDataFetcher from '../useDataFetcher';

import colors from '../../assets/colors.module.scss';
import styles from './userMenu.module.scss';

// Mounted only while the popover shows it, so its requests run when the menu opens rather than on
// every page.
const UserMenuContent = ({ onUpdatePassword, onClose }) => {
  const { user, userLogout, userReLogin, userProfile } = useCms();

  const [isSubmitting, setIsSubmitting] = useState();
  const [showOutOfOfficeDialog, setShowOutOfOfficeDialog] = useState(false);
  const [showShareNotifications, setShowShareNotifications] = useState(false);
  const [isEditOOO, setIsEditOOO] = useState(false);
  const [showPersonalEmailSignatureDialog, setShowPersonalEmailSignatureDialog] = useState(false);

  const {
    isLoading,
    isError,
    data: sharedUsersIds,
    reloadData: reloadSharedUsers,
  } = useDataFetcher('/api/v1/users/me/share_notifications');

  const {
    isError: isErrorOooData,
    isLoading: isLoadingOooData,
    data: oooData,
    reloadData: reloadOutOfOfficeData,
  } = useDataFetcher('/api/v1/users/me/out_of_office');

  // `is_enabled` is the organization's 'Allow users to set a personal email signature' setting.
  // Reading it from the server rather than re-deriving it client-side keeps the menu in step with
  // what a save would accept.
  const { data: personalEmailSignatureData } = useDataFetcher('/api/v1/users/me/email_signature');
  const isPersonalEmailSignatureEnabled = personalEmailSignatureData?.is_enabled ?? false;

  const isUserOutOfOffice = user.is_out_of_office ?? false;
  const userPreviousOOOEmailContent = oooData?.ooo_email_content;

  let initialDialogData = null;

  if (isEditOOO) {
    initialDialogData = oooData;
  } else {
    initialDialogData = {
      ooo_email_content: userPreviousOOOEmailContent || '',
    };
  }

  const handleToggleOutOfOffice = async (isOutOfOffice) => {
    setIsSubmitting(true);

    try {
      if (isOutOfOffice) {
        setIsEditOOO(false);
        setShowOutOfOfficeDialog(true);
      } else {
        await axios.delete('/api/v1/users/me/out_of_office');
        await userReLogin();
      }
    } catch (error) {
      await reportAxiosError(error);
    }
    setIsSubmitting(false);
  };

  const handleSetOutOfOffice = async (values) => {
    try {
      const httpMethod = isEditOOO ? 'patch' : 'post';
      await axios[httpMethod]('/api/v1/users/me/out_of_office', values);
      await reloadOutOfOfficeData();
      await userReLogin();
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    }
  };

  const handleShareNotifications = async (shouldShare) => {
    setIsSubmitting(true);

    if (shouldShare) {
      setShowShareNotifications(true);
    } else {
      try {
        await axios.post('/api/v1/users/me/share_notifications', {
          share_notification_with_users_ids: [],
        });
        await reloadSharedUsers();
      } catch (error) {
        reportAxiosError(error);
      }
    }

    setIsSubmitting(false);
  };

  const handleEditOutOfOffice = () => {
    setIsEditOOO(true);
    setShowOutOfOfficeDialog(true);
  };

  return (
    <CardDialog
      width="350px"
      onClose={onClose}
      containerClassName={styles.userMenuContainer}
      contentStyle={{ paddingBottom: 0 }}
    >
      <Grid container className={styles.headerContainer}>
        <Grid item xs={2} className={styles.avatarContainer}>
          <Avatar src={`/api/v1/organizations/${user.organization_id}/users/${user.id}/avatar`} className="bg-teal-700">
            {user.first_name[0]}
            {user.last_name[0]}
          </Avatar>
        </Grid>
        <Grid item xs={10}>
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM}>
            {user.username}
          </Text>
          <EmailsDisplay
            email_addresses={user.claim_emails}
            colorVariant={EmailsDisplay.COLOR_VARIANTS.SECONDARY}
            variant={EmailsDisplay.VARIANTS.XXS}
          />
          {user.twilio_phone_number && <Label>{user.twilio_phone_number}</Label>}
        </Grid>
      </Grid>
      <div className="mt-6 mb-12 flex flex-col gap-16 rounded bg-blueSky-100 px-16 py-12">
        <FeatureEnabled featureFlag={CONFIGURATION_FEATURES_NAMES.PERMISSIONS_ENFORCEMENT}>
          <PropertyDetails title="Your Permission Profile" text={userProfile?.display_name} />
        </FeatureEnabled>
        <FeatureEnabled featureFlag={CONFIGURATION_FEATURES_NAMES.ORG_HIERARCHY_WORKFLOWS}>
          <PropertyDetails title="Unit Member" text={user.member_of_unit?.name} />
          {!!user.leader_of_units.length && (
            <PropertyDetailsRow>
              <PropertyDetails title="Unit Leader" text={user.leader_of_units.map((unit) => unit.name)} useChips />
            </PropertyDetailsRow>
          )}
        </FeatureEnabled>
      </div>
      <div className={styles.rowContainer}>
        <div className={styles.iconWithTextContainer}>
          <MinusCircleIcon iconColor={colors.textPrimary} />
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
            Out of Office
          </Text>
        </div>
        <div className="flex items-center">
          {isUserOutOfOffice ? (
            <span className="mr-8">
              <InlineIconButton
                icon={PencilIcon}
                onClick={handleEditOutOfOffice}
                tooltipTitle="Edit"
                className="m-4 hover:text-teal-700"
                iconColor="currentColor"
              />
            </span>
          ) : null}
          <Switch
            disabled={isSubmitting}
            checked={isUserOutOfOffice}
            onChange={(e, checked) => handleToggleOutOfOffice(checked)}
          />
        </div>
      </div>

      <Divider />

      <div className={styles.rowContainer}>
        <div className={styles.iconWithTextContainer}>
          <ReminderIcon iconColor={colors.textPrimary} />
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
            Share notifications
          </Text>
        </div>
        <Switch
          disabled={isSubmitting || isLoading || isError}
          checked={sharedUsersIds?.length > 0}
          onChange={(e, checked) => handleShareNotifications(checked)}
        />
      </div>

      {isPersonalEmailSignatureEnabled && (
        <MenuItem
          disabled={isSubmitting}
          onClick={() => setShowPersonalEmailSignatureDialog(true)}
          className={styles.menuItem}
        >
          <div className={styles.iconWithTextContainer}>
            <SignatureIcon />
            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
              Personal Email Signatures
            </Text>
          </div>
        </MenuItem>
      )}

      {onUpdatePassword && (
        <MenuItem
          disabled={isSubmitting}
          onClick={() => {
            onUpdatePassword();
            onClose();
          }}
          className={styles.menuItem}
        >
          <div className={styles.iconWithTextContainer}>
            <LockedIcon />
            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
              Update Password
            </Text>
          </div>
        </MenuItem>
      )}

      <Divider />

      <MenuItem className={styles.menuItem} disabled={isSubmitting} onClick={userLogout}>
        <div className={styles.iconWithTextContainer}>
          <SignOutIcon />
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
            Logout
          </Text>
        </div>
      </MenuItem>

      {showOutOfOfficeDialog && !isLoadingOooData ? (
        <OutOfOfficeDialog
          user={user}
          onSubmit={handleSetOutOfOffice}
          onClose={() => {
            setIsSubmitting(false);
            setShowOutOfOfficeDialog(false);
          }}
          currentSharedUsersIds={sharedUsersIds || []}
          outOfOfficeData={initialDialogData}
          isErrorOooData={isErrorOooData}
          isLoadingOooData={isLoadingOooData}
        />
      ) : null}
      {showShareNotifications && (
        <ShareNotificationsContainer
          onClose={() => setShowShareNotifications(false)}
          onUpdate={reloadSharedUsers}
          currentSharedUsersIds={sharedUsersIds}
        />
      )}
      {showPersonalEmailSignatureDialog && (
        <PersonalEmailSignatureDialog onClose={() => setShowPersonalEmailSignatureDialog(false)} />
      )}
    </CardDialog>
  );
};

UserMenuContent.propTypes = {
  onUpdatePassword: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

// Stays mounted, so the popover can run its opening and closing transitions.
const UserMenu = ({ onUpdatePassword, anchorEl, onClose, id }) => (
  <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={onClose} id={id} aria-label="User menu">
    <UserMenuContent onUpdatePassword={onUpdatePassword} onClose={onClose} />
  </Popover>
);

UserMenu.propTypes = {
  onUpdatePassword: PropTypes.func,
  anchorEl: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  /** The popover's id, which the avatar's aria-controls names. */
  id: PropTypes.string,
};

export default UserMenu;
